import React, { useState, useEffect } from 'react';
import './Exchange.css';
import '../components/UserInfo.css';
import { userAPI } from '../api/user';
import { exchangeAPI } from '../api/exchange';
import UserInfo from '../components/UserInfo';
import CartIcon from '../CartIcon';
import CartPanel from '../CartPanel';
import OrderAddressModal from '../OrderAddressModal';
import { useCart } from '../CartContext';

function Exchange() {
  const [score, setScore] = useState(0);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCartPanel, setShowCartPanel] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [checkoutItems, setCheckoutItems] = useState([]);
  const [checkoutTotalPrice, setCheckoutTotalPrice] = useState(0);

  const { addToCart, removeMultipleFromCart } = useCart();

  const showToastMessage = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2500);
  };

  useEffect(() => {
    getUserInfo();
    getProducts();
  }, []);

  const getUserInfo = async () => {
    try {
      const response = await userAPI.getUserInfo();
      setScore(response.score);
    } catch (error) {
      console.error('获取用户信息失败', error);
    }
  };

  const getProducts = async () => {
    try {
      const response = await exchangeAPI.getProducts();
      setProducts(response);
    } catch (error) {
      console.error('获取商品列表失败', error);
    }
  };

  const handleAddToCart = (product) => {
    if (score < product.price) {
      showToastMessage(`积分不足！需要 ${product.price} 元宝，当前只有 ${score} 元宝`, 'error');
      return;
    }

    addToCart({ productId: product.id });

    showToastMessage(`已将"${product.name}"加入购物车！`, 'success');
  };

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
    setCurrentImageIndex(0);
  };

  const handleCloseDetails = () => {
    setSelectedProduct(null);
    setCurrentImageIndex(0);
  };

  const handleCheckout = (selectedItems) => {
    setShowCartPanel(false);
    const items = selectedItems || [];
    setCheckoutItems(items);
    setCheckoutTotalPrice(items.reduce((sum, item) => sum + (item.price * item.quantity), 0));
    setShowAddressModal(true);
  };

  const handleConfirmOrder = async (orderData) => {
    try {
      if (!orderData.items || !Array.isArray(orderData.items) || orderData.items.length === 0) {
        showToastMessage('订单商品信息为空', 'error');
        return;
      }

      const totalPrice = orderData.totalPrice || orderData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      setCheckoutTotalPrice(totalPrice);

      if (score < totalPrice) {
        showToastMessage(`积分不足！需要 ${totalPrice} 元宝，当前只有 ${score} 元宝`, 'error');
        return;
      }

      const orderItems = orderData.items.map(item => ({
        productId: item.productId,
        quantity: item.quantity || 1,
        price: item.price
      }));

      const response = await exchangeAPI.createOrder({
        items: orderItems,
        address: orderData.address,
        totalPrice
      });

      console.log('下单成功:', response);

      const userInfo = await userAPI.getUserInfo();
      setScore(userInfo.score);

      setShowAddressModal(false);
      setCheckoutItems([]);
      const orderedProductIds = orderData.items.map(item => item.productId || item.id);
      await removeMultipleFromCart(orderedProductIds);
      showToastMessage('下单成功！', 'success');
    } catch (error) {
      console.error('下单失败:', error);
      showToastMessage(error.response?.data?.message || '下单失败，请重试', 'error');
    }
  };

  const parseDetailImages = (detailImagesStr) => {
    if (!detailImagesStr) return [];
    try {
      return JSON.parse(detailImagesStr);
    } catch (e) {
      return [];
    }
  };

  return (
    <div className="exchange-container">
      <div className="exchange-header">
        <h2>🎁 积分兑换商城</h2>
        <div className="header-right">
          <div className="score-info">
            <span>当前积分：</span>
            <span className="score">{score}</span>
            <span> 元宝</span>
          </div>
          <UserInfo currentPage="exchange" />
        </div>
      </div>

      <div className="products-section">
        <h3 className="section-title">精选好物</h3>
        <div className="product-grid">
          {products.map(product => (
            <div key={product.id} className="product-card" onClick={() => handleViewDetails(product)}>
              <div className="card-image-wrapper">
                <img
                  src={product.imageUrl || product.image || '/placeholder.png'}
                  alt={product.name}
                  className="product-image"
                  onError={(e) => e.target.src = '/placeholder.png'}
                />
                <div className="image-overlay">
                  <span className="view-details-btn">查看详情</span>
                </div>
              </div>
              <div className="card-body">
                <h4 className="product-name">{product.name}</h4>
                <div className="product-meta">
                  <span className="product-price">{product.price} <small>元宝</small></span>
                  <span className={`product-stock ${product.stock > 0 ? 'in-stock' : 'out-stock'}`}>
                    库存: {product.stock}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {products.length === 0 && (
          <div className="empty-products">
            <p>暂无商品~敬请期待</p>
          </div>
        )}
      </div>

      {selectedProduct && (
        <div className="product-detail-modal" onClick={handleCloseDetails}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={handleCloseDetails}>✕</button>

            <div className="detail-gallery">
              {(() => {
                const images = parseDetailImages(selectedProduct.detailImages);
                const mainImage = selectedProduct.imageUrl || selectedProduct.image;
                const allImages = mainImage ? [mainImage, ...images] : images;

                if (allImages.length > 1) {
                  return (
                    <>
                      <img
                        src={allImages[currentImageIndex]}
                        alt={`${selectedProduct.name} - 图${currentImageIndex + 1}`}
                        className="gallery-main-image"
                      />
                      <div className="gallery-thumbs">
                        {allImages.map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`缩略图${idx + 1}`}
                            className={`thumb-image ${idx === currentImageIndex ? 'active' : ''}`}
                            onClick={() => setCurrentImageIndex(idx)}
                          />
                        ))}
                      </div>
                    </>
                  );
                } else {
                  return (
                    <img
                      src={allImages[0] || '/placeholder.png'}
                      alt={selectedProduct.name}
                      className="gallery-single-image"
                    />
                  );
                }
              })()}
            </div>

            <div className="detail-info">
              <h2>{selectedProduct.name}</h2>
              
              <div className="detail-price-row">
                <span className="detail-price">{selectedProduct.price}</span>
                <span className="price-unit">元宝</span>
                <span className={`stock-badge ${selectedProduct.stock > 0 ? 'available' : 'unavailable'}`}>
                  {selectedProduct.stock > 0 ? `库存充足 (${selectedProduct.stock})` : '缺货'}
                </span>
              </div>

              <div className="detail-description">
                <h4>商品详情</h4>
                <p>{selectedProduct.description || '暂无详细描述'}</p>
              </div>

              <button
                className="add-to-cart-btn"
                onClick={() => {
                  handleAddToCart(selectedProduct);
                  handleCloseDetails();
                }}
                disabled={selectedProduct.stock <= 0}
              >
                🛒 加入购物车
              </button>
            </div>
          </div>
        </div>
      )}

      <CartIcon onToggle={() => setShowCartPanel(!showCartPanel)} />

      <CartPanel
        isOpen={showCartPanel}
        onClose={() => setShowCartPanel(false)}
        onCheckout={handleCheckout}
      />

      <OrderAddressModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSubmit={handleConfirmOrder}
        selectedItems={checkoutItems}
        totalPrice={checkoutTotalPrice}
      />

      {showToast && (
        <div className="modal-overlay" onClick={() => setShowToast(false)} style={{ zIndex: 10000 }}>
          <div className={`lively-modal toast-modal ${toastType}`} onClick={e => e.stopPropagation()}>
            <div className="toast-icon">{toastType === 'success' ? '✅' : '❌'}</div>
            <p className="toast-message">{toastMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Exchange;
